import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMergedRefs } from '@grammarly/react-utils'
import { VBar, type VBarProps } from '../../vbar/VBar'
import { type ClusterItemMetadata, useVBarStackItem } from './VBarStackAPI'
import { defaultVBarFontMetrics, VBarFontMetricsContext } from '../../vbar/VBarFontMetricsContext'
import { PointerPositionContext } from './PointerPositionContext'
import classes from './VBar.module.scss'

export interface PositionedVBarProps extends VBarProps {
  readonly id: string
  readonly top: number
  readonly anchorHeight: number
  readonly left?: number
  readonly width?: number
  readonly zIndex?: number
  /** Optional data attributes that should be attached to the component */
  readonly dataset?: Record<`data-${string}`, string>
}

/**
 * This component renders a VBar item in a vertical stack.
 * This component needs to be within a positioned container as it will position itself absolutely within that container.
 * If a parent <VBarStack> container is used, the container will manage overlap and clustering of vBars.
 */
export const PositionedVBar = memo(
  forwardRef<HTMLElement, PositionedVBarProps>(
    ({ id, top, left, width, anchorHeight, onHover, zIndex, dataset, ...props }, forwardedRef) => {
      const ref = useRef<HTMLElement | null>(null)
      const mergedRef = useMergedRefs(ref, forwardedRef)
      const isHovered = useRef(false)

      const [active, setActive] = useState(props.isActive ?? false)

      const pointerPositionContext = useContext(PointerPositionContext)
      const fontMetricsContext = useContext(VBarFontMetricsContext)
      const lineHeight =
        props.lineHeight ?? fontMetricsContext.lineHeight ?? defaultVBarFontMetrics.lineHeight

      useLayoutEffect(() => {
        setActive(props.isActive || isHovered.current)
      }, [props.isActive])

      const { color, strokeColor, fillColor, primaryTextColor, secondaryTextColor, maxTextWidth } =
        props

      const clusterItemData = useMemo<ClusterItemMetadata>(
        () => ({
          color,
          strokeColor,
          fillColor,
          primaryTextColor,
          secondaryTextColor,
          maxTextWidth,
          lineHeight,
          left,
          width,
          zIndex,
        }),
        [
          color,
          strokeColor,
          fillColor,
          primaryTextColor,
          secondaryTextColor,
          maxTextWidth,
          lineHeight,
          left,
          width,
          zIndex,
        ],
      )

      const primaryTextLines = props.primaryText ? 1 : 0
      const secondaryTextLines = props.secondaryText ? 1 : 0

      const item = useVBarStackItem({
        id,
        top,
        anchorHeight,
        active,
        clusterItemData,
        minContentHeight: Math.ceil(
          Math.max(primaryTextLines + secondaryTextLines, 1) * lineHeight,
        ),
      })

      const overriddenOnHover = useCallback(
        (hovered: boolean) => {
          if (hovered === isHovered.current) return

          isHovered.current = hovered
          setActive(hovered || props.isActive === true)
          onHover?.(hovered)
        },
        [onHover, props.isActive],
      )

      // When a cluster is expanded/collapsed and VBars are translated and transitioned between hidden and visible,
      // we need to make sure that the hover state is updated and reported correctly.
      // This is because the pointer is not moving, so pointerEnter/pointerLeave will not emit (and thus onHover)
      const checkIfHoverStateChanged = useCallback(() => {
        if (!ref.current) return
        // No need to check non-clustered items.
        if (item.clusterId === null) return

        const position = pointerPositionContext.getLastPointerPosition()
        if (!position) return

        if (isPositionWithinElement(position, ref.current)) {
          overriddenOnHover(true)
        } else {
          overriddenOnHover(false)
        }
      }, [item.clusterId, overriddenOnHover, pointerPositionContext])

      // Animate expansion/collapse from a cluster by triggering the CSS transition.
      useEffect(() => {
        ref.current?.style.setProperty('--translate-y', `${-item.clusterOffset}px`)
        const frameId = requestAnimationFrame(() => {
          ref.current?.setAttribute('data-collapsed', item.kind === 'collapsed' ? 'true' : 'false')
        })
        return () => cancelAnimationFrame(frameId)
      }, [item.kind, item.clusterOffset])

      // Limit the max-height to at most 10 lines.
      const maxHeight = Math.min(lineHeight * 10, item.maxHeight)

      return (
        <div
          className={classes.vBar}
          style={{
            top: item.top,
            left,
            width,
            height: maxHeight,
            maxHeight,
            zIndex,
          }}
          ref={mergedRef}
          onTransitionEnd={checkIfHoverStateChanged}
          onAnimationEnd={checkIfHoverStateChanged}
          {...dataset}
        >
          <VBar
            {...props}
            anchorHeight={item.anchorHeight}
            onHover={overriddenOnHover}
            lineHeight={lineHeight}
          />
        </div>
      )
    },
  ),
)

function isPositionWithinElement(
  position: { readonly clientX: number; readonly clientY: number },
  element: HTMLElement,
): boolean {
  const rect = element.getBoundingClientRect()
  return (
    position.clientX >= rect.left &&
    position.clientX <= rect.right &&
    position.clientY >= rect.top &&
    position.clientY <= rect.bottom
  )
}
